import { useEffect, useMemo, useRef, useState } from "react";

import type { TileCatalogPayload } from "@west-marches/shared";
import { getMapChromeTheme } from "@/components/mapChromeTheme";
import { cn } from "@/lib/classNames";

import {
  buildTaxonomySectionPath,
  getChildTaxonomySections,
  getRootTaxonomySections,
  type TileCatalogBrowserSection,
} from "./tileCatalogBrowserSections";

interface BreadcrumbLevel {
  key: string;
  label: string;
  path: TileCatalogBrowserSection[];
  options: TileCatalogBrowserSection[];
}

interface TileTaxonomyBreadcrumbsProps {
  payload: TileCatalogPayload;
  sectionPath: TileCatalogBrowserSection[];
  hiddenTileSetIds?: string[];
  onNavigatePath: (path: TileCatalogBrowserSection[]) => void;
}

export default function TileTaxonomyBreadcrumbs({
  payload,
  sectionPath,
  hiddenTileSetIds = [],
  onNavigatePath,
}: TileTaxonomyBreadcrumbsProps) {
  const theme = getMapChromeTheme();
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hiddenTileSetIdSet = useMemo(() => new Set(hiddenTileSetIds), [hiddenTileSetIds]);
  const taxonomyPath = useMemo(
    () => sectionPath.filter((section) => section.kind === "taxonomy"),
    [sectionPath],
  );
  const rootOptions = useMemo(
    () => getRootTaxonomySections(payload, hiddenTileSetIdSet),
    [hiddenTileSetIdSet, payload],
  );
  const breadcrumbLevels = useMemo<BreadcrumbLevel[]>(() => {
    const levels: BreadcrumbLevel[] = [
      {
        key: "root",
        label: "Tiles",
        path: [],
        options: rootOptions,
      },
    ];

    taxonomyPath.forEach((section, index) => {
      const parentSection = taxonomyPath[index - 1] ?? null;
      const options =
        parentSection === null
          ? rootOptions
          : getChildTaxonomySections(payload, parentSection.id, hiddenTileSetIdSet);

      levels.push({
        key: section.id,
        label: section.label,
        path: taxonomyPath.slice(0, index + 1),
        options,
      });
    });

    return levels;
  }, [hiddenTileSetIdSet, payload, rootOptions, taxonomyPath]);

  if (rootOptions.length === 0) {
    return null;
  }

  function clearPendingClose() {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }

  function scheduleClose(levelKey: string) {
    clearPendingClose();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenuKey((current) => (current === levelKey ? null : current));
      closeTimeoutRef.current = null;
    }, 120);
  }

  useEffect(() => () => clearPendingClose(), []);

  return (
    <nav aria-label="Tile taxonomy breadcrumbs" className="relative z-20 mb-3">
      <div className={theme.breadcrumbRail}>
        {breadcrumbLevels.map((level, index) => {
          const isCurrentLevel =
            (level.key === "root" && taxonomyPath.length === 0) ||
            (index === breadcrumbLevels.length - 1 && taxonomyPath.length > 0);
          const menuIsOpen = openMenuKey === level.key;

          return (
            <div key={level.key} className="flex items-center gap-1">
              <div
                className="relative"
                onMouseEnter={() => {
                  clearPendingClose();
                  setOpenMenuKey(level.key);
                }}
                onMouseLeave={() => scheduleClose(level.key)}
                onFocus={() => {
                  clearPendingClose();
                  setOpenMenuKey(level.key);
                }}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    scheduleClose(level.key);
                  }
                }}
              >
                <button
                  type="button"
                  aria-haspopup={level.options.length > 0 ? "menu" : undefined}
                  aria-expanded={level.options.length > 0 ? menuIsOpen : undefined}
                  aria-current={isCurrentLevel ? "page" : undefined}
                  className={cn(
                    theme.breadcrumbButton,
                    isCurrentLevel ? theme.breadcrumbButtonCurrent : null,
                  )}
                  onClick={() => {
                    clearPendingClose();
                    setOpenMenuKey(null);
                    onNavigatePath(level.path);
                  }}
                >
                  <span>{level.label}</span>
                </button>
                {menuIsOpen && level.options.length > 0 ? (
                  <div
                    role="menu"
                    aria-label={`${level.label} options`}
                    className={theme.breadcrumbMenu}
                  >
                    {level.options.map((option) => {
                      const optionPath = buildTaxonomySectionPath(payload, option.id, hiddenTileSetIdSet);
                      const levelIndex = Math.max(level.path.length - 1, 0);
                      const optionIsCurrent =
                        taxonomyPath.length > 0 && taxonomyPath[levelIndex]?.id === option.id;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          role="menuitem"
                          className={cn(
                            theme.breadcrumbMenuItem,
                            optionIsCurrent ? theme.breadcrumbMenuItemCurrent : null,
                          )}
                          onClick={() => {
                            clearPendingClose();
                            setOpenMenuKey(null);
                            onNavigatePath(optionPath);
                          }}
                        >
                          <span>{option.label}</span>
                          {optionIsCurrent ? (
                            <span aria-hidden className={`text-xs ${theme.textSubtle}`}>
                              Current
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              {index < breadcrumbLevels.length - 1 ? (
                <span aria-hidden className={`px-0.5 text-xs font-semibold ${theme.textSubtle}`}>
                  {">"}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
